// The canonical form both sides sign and hash (spec section 6.1, "Signed
// operations"; API contract section 5): a flat JSON object, keys sorted by
// UTF-16 code unit, no whitespace, values limited to what JSON.stringify
// renders identically in every runtime. This mirrors the API's
// src/features/lessons/encryption/canonical.ts, and canonical.spec.js carries
// the vectors both must satisfy.
import { utf8Encode } from './encoding';
import { sha256Base64Url } from './webcrypto';

/** The operations the API verifies a bundle-key signature for, in contract order. */
export const SIGNED_OPS = Object.freeze([
  'register',
  'approve',
  'revoke',
  'recovery',
  // #1694, between `recovery` and `reset` because that is the order plan
  // section 6.1 lists the contract in.
  //
  // THREE OPS, NOT ONE (decision 49). Commit and abort are both signed by the
  // PENDING bundle key, so a single `rotate` op with the same field set would
  // give them byte-identical canonical messages — and an abort signature could
  // then be presented to commit, which revokes every other device. Distinct op
  // names make that impossible in the message itself rather than relying on a
  // discriminating field somebody has to remember to include.
  //
  // Only the NAMES are settled here: no `FIELDS_FOR.*` goes into signer.js
  // until the API defines the field sets, because a signature over the wrong
  // fields is refused as a 403 with nothing in it to say which field was wrong.
  'rotate-begin',
  'rotate-commit',
  // FOUR, since #1730 gave an abort a first half. `rotate-abort-begin` flips the
  // write gate so the walk can run backwards; it carries the same single field
  // as `rotate-abort` and is signed by the same key, so it needs a name of its
  // own for the reason the three above do.
  //
  // ⚠️ THIS LIST IS SEPARATE FROM `FIELDS_FOR` IN signer.js, AND BOTH ARE
  // REQUIRED. The op was added there and not here, so every attempt to cancel a
  // rotation threw `canonicalMessage: "rotate-abort-begin" is not a signed
  // operation` — before any request left the device, which is why no status
  // code and no server log ever mentioned it. A unit test that asserts the
  // FIELDS_FOR keys does not catch that; only signing the op does.
  'rotate-abort-begin',
  'rotate-abort',
  // #1695 / api#1125. TWO ops, not one with a discriminating field: setting a
  // passphrase and removing one differ in their field sets already, so their
  // messages could not collide — but removal is the only operation here that
  // takes a recovery path AWAY, and giving it a name of its own closes the
  // question rather than leaving it to a field somebody has to remember.
  'passphrase',
  'passphrase-remove',
  'reset',
  'cancel',
]);

const RESERVED = Object.freeze(['op', 'memberId', 'nonce']);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isCanonicalValue(value) {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

/**
 * Same construction as the API, on purpose: the sorted keys are inserted into a
 * plain object and stringified, and JavaScript emits integer-like keys ("2",
 * "10") first regardless of insertion order. No contract field is numeric, and
 * both sides drift together if one ever is; do not "fix" this here alone.
 *
 * @param {Record<string, string | number | boolean | null>} fields
 * @returns {string}
 */
export function canonicalJson(fields) {
  const sorted = {};
  for (const key of Object.keys(fields).sort()) {
    const value = fields[key];
    if (!isCanonicalValue(value)) {
      throw new TypeError(`canonicalJson: "${key}" must be a string, number, boolean or null`);
    }
    sorted[key] = value;
  }
  return JSON.stringify(sorted);
}

/**
 * @param {'register' | 'approve' | 'revoke' | 'recovery' | 'reset' | 'cancel'} op
 * @param {number} memberId
 * @param {string} nonce from POST /api/me/encryption/challenge
 * @param {Record<string, string | number | boolean | null>} [fields] the request's own fields
 * @returns {string} the exact bytes (as a string) the signature covers
 */
export function canonicalMessage(op, memberId, nonce, fields = {}) {
  if (!SIGNED_OPS.includes(op)) {
    throw new TypeError(`canonicalMessage: "${op}" is not a signed operation`);
  }
  for (const key of RESERVED) {
    if (key in fields) throw new TypeError(`canonicalMessage: "${key}" is reserved`);
  }
  return canonicalJson({ ...fields, op, memberId, nonce });
}

/**
 * SHA-256 of the canonical JSON, base64url: how a wrapping is named inside a
 * signed message (`wrappedMkHash`, `recoveryWrappingHash`).
 *
 * @param {Record<string, string | number | boolean | null>} fields
 * @returns {Promise<string>}
 */
export function hashCanonical(fields) {
  return sha256Base64Url(utf8Encode(canonicalJson(fields)));
}

/**
 * The devices a `rotate-commit` keeps, as the ONE string the signature covers
 * (api#1156): `<deviceId>:<hash of its new wrapping>` per device, sorted,
 * comma-joined. Canonical fields are flat, so a list has to become a string;
 * sorting makes the order sent irrelevant; hashing the wrapping binds every
 * byte of it while keeping the message short. Byte-identical to the API's
 * `keepDigest` in canonical.ts, or no commit that keeps a device verifies.
 *
 * @param {Array<{ deviceId: string, wrappedMk: object }>} keep
 * @returns {Promise<string>} '' for nothing kept
 */
/**
 * The device ids a `rotate-begin` keeps, as one canonical string the signature
 * covers: unique, sorted, comma-joined. Unsigned, a relay could change WHICH
 * devices a rotation keeps. Byte-identical to the API's `keepListDigest`.
 *
 * @param {string[]} ids
 * @returns {string}
 */
export function keepListDigest(ids) {
  return [...new Set(ids ?? [])].sort().join(',');
}

export async function keepDigest(keep) {
  const parts = await Promise.all(
    (keep ?? []).map(
      async ({ deviceId, wrappedMk }) => `${deviceId}:${await hashCanonical({ ...wrappedMk })}`,
    ),
  );
  return parts.sort().join(',');
}
